// ============================================================================
// AUTH CONTEXT — Central auth state with memoized permission engine
// ============================================================================
// Provides auth state, permission engine, and scope management to all components.
// Supports both legacy mode (REACT_APP_AUTH_MODE=legacy) and tenant-hub mode.

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
  type ReactNode,
} from 'react';
import { authService } from '../api/services/authService';
import { tokenManager } from '../auth/tokenManager';
import { STORAGE_KEYS } from '../auth/storageKeys';
import { pickPrimaRole, formatRoleName } from '../config/rolePermissions';
import {
  createPermissionEngine,
  buildNavigationFromPermissions,
  buildModulesConfigFromPermissions,
} from '../auth/permissionEngine';
import type { PermissionEngine } from '../auth/permissionEngine';
import type {
  UserPermissionContext,
  UserScopeConfiguration,
  DiscoverResponse,
  AccessibleEntity,
  AccessibleBusinessUnit,
  AccessibleFinancialYear,
} from '../api/types/auth.types';

// ============================================================================
// Types
// ============================================================================

export interface AuthUser {
  userId: string;
  userName: string;
  userEmail: string;
  firstName: string;
  lastName: string;
  fullName: string;
  userRole: string;
}

export interface AuthContextValue {
  // === State ===
  isAuthenticated: boolean;
  isLoading: boolean;
  user: AuthUser | null;
  tenantContext: { tenantId: string; appId: string } | null;
  permissionContext: UserPermissionContext | null;
  scopeConfiguration: UserScopeConfiguration | null;

  // === Auth Actions ===
  discover: (
    username: string,
    password: string,
    clientId?: string
  ) => Promise<DiscoverResponse>;
  authenticate: (
    username: string,
    password: string,
    tenantId: string,
    appId: string
  ) => Promise<void>;
  logout: () => void;
  refreshPermissions: () => Promise<void>;

  // === Permission Engine (O(1) lookups) ===
  permissionEngine: PermissionEngine;

  // === Scope Management ===
  selectedEntity: { id: string; name: string } | null;
  selectedBusinessUnit: { id: string; name: string } | null;
  selectedFinancialYear: { id: string; name: string; code: string } | null;
  setSelectedEntity: (id: string, name: string) => void;
  setSelectedBusinessUnit: (id: string, name: string) => void;
  setSelectedFinancialYear: (id: string, name: string, code: string) => void;
  accessibleEntities: AccessibleEntity[];
  accessibleBusinessUnits: AccessibleBusinessUnit[];
  accessibleFinancialYears: AccessibleFinancialYear[];
}

// ============================================================================
// Context
// ============================================================================

const AuthContext = createContext<AuthContextValue | null>(null);

// ============================================================================
// Auth Mode Helper
// ============================================================================

const getAuthMode = (): 'tenant-hub' | 'legacy' => {
  // Fail-secure: ONLY an explicit REACT_APP_AUTH_MODE=legacy enables the mock
  // login. Unset/unknown values resolve to tenant-hub (real auth).
  return process.env.REACT_APP_AUTH_MODE === 'legacy' ? 'legacy' : 'tenant-hub';
};

// ============================================================================
// Provider
// ============================================================================

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [tenantContext, setTenantContext] = useState<{ tenantId: string; appId: string } | null>(null);
  const [permissionContext, setPermissionContext] = useState<UserPermissionContext | null>(null);
  const [scopeConfiguration, setScopeConfiguration] = useState<UserScopeConfiguration | null>(null);

  // Scope selections
  const [selectedEntity, setSelectedEntityState] = useState<{ id: string; name: string } | null>(null);
  const [selectedBusinessUnit, setSelectedBUState] = useState<{ id: string; name: string } | null>(null);
  const [selectedFinancialYear, setSelectedFYState] = useState<{
    id: string;
    name: string;
    code: string;
  } | null>(null);

  // Permission engine rebuilt ONLY when permissionContext changes
  const permissionEngine = useMemo(
    () => createPermissionEngine(permissionContext),
    [permissionContext]
  );

  // In tenant-hub mode, derive from token state (reactive via user/tenantContext deps).
  // In legacy mode, always return true — legacy login manages its own auth state
  // via localStorage, and route protection is handled by ProtectedRoute passthrough.
  const isAuthenticated = useMemo(() => {
    if (getAuthMode() === 'tenant-hub') {
      return !!user && tokenManager.isAuthenticated();
    }
    // Legacy mode: not managed by AuthContext — always return true
    return true;
  }, [user]);

  // === Session Recovery on Mount ===
  useEffect(() => {
    const recoverSession = () => {
      try {
        if (getAuthMode() === 'tenant-hub') {
          const token = tokenManager.getAccessToken();
          if (token && !tokenManager.isTokenExpired(token)) {
            // Hydrate state from localStorage
            const storedPermissions = localStorage.getItem(STORAGE_KEYS.PERMISSIONS);
            if (storedPermissions) {
              setPermissionContext(JSON.parse(storedPermissions));
            }

            const storedScope = localStorage.getItem(STORAGE_KEYS.SCOPE_CONFIG);
            if (storedScope) {
              const scope = JSON.parse(storedScope) as UserScopeConfiguration;
              setScopeConfiguration(scope);
            }

            // Recover user info
            const userName = localStorage.getItem(STORAGE_KEYS.USER_NAME);
            const userEmail = localStorage.getItem(STORAGE_KEYS.USER_EMAIL);
            if (userName) {
              setUser({
                userId: tokenManager.getUserId() || '',
                userName: userName,
                userEmail: userEmail || '',
                firstName: localStorage.getItem(STORAGE_KEYS.USER_FIRST_NAME) || '',
                lastName: localStorage.getItem(STORAGE_KEYS.USER_LAST_NAME) || '',
                fullName: localStorage.getItem(STORAGE_KEYS.FULL_NAME) || userName,
                userRole: localStorage.getItem(STORAGE_KEYS.USER_ROLE) || '',
              });
            }

            // Recover tenant context
            const tenantId = tokenManager.getTenantId();
            const appId = tokenManager.getAppId();
            if (tenantId && appId) {
              setTenantContext({ tenantId, appId });
            }

            // Recover scope selections
            const entityId = localStorage.getItem(STORAGE_KEYS.SELECTED_ENTITY_ID);
            const entityName = localStorage.getItem(STORAGE_KEYS.SELECTED_ENTITY_NAME);
            if (entityId && entityName) {
              setSelectedEntityState({ id: entityId, name: entityName });
            }

            const buId = localStorage.getItem(STORAGE_KEYS.SELECTED_BU_ID);
            const buName = localStorage.getItem(STORAGE_KEYS.SELECTED_BU_NAME);
            if (buId && buName) {
              setSelectedBUState({ id: buId, name: buName });
            }

            const fyId = localStorage.getItem(STORAGE_KEYS.SELECTED_FY_ID);
            const fyName = localStorage.getItem(STORAGE_KEYS.SELECTED_FY_NAME);
            const fyCode = localStorage.getItem(STORAGE_KEYS.SELECTED_FY_CODE);
            if (fyId && fyName) {
              setSelectedFYState({ id: fyId, name: fyName, code: fyCode || '' });
            }
          } else if (tokenManager.getRefreshToken()) {
            // Attempt silent refresh
            authService
              .refreshTenantHubTokens()
              .then(() => recoverSession())
              .catch(() => {
                tokenManager.clearAll();
              });
          }
        }
      } catch {
        // Silently fail on recovery errors
      } finally {
        setIsLoading(false);
      }
    };

    recoverSession();
  }, []);

  // === Auth Actions ===

  const discover = useCallback(
    async (
      username: string,
      password: string,
      clientId?: string
    ): Promise<DiscoverResponse> => {
      const response = await authService.discover({
        username,
        password,
        clientId,
      });

      if (!response.success || !response.data) {
        throw new Error(response.message || 'Discovery failed');
      }

      return response.data;
    },
    []
  );

  const authenticate = useCallback(
    async (
      username: string,
      password: string,
      tenantId: string,
      appId: string
    ): Promise<void> => {
      // Clear stale session data from any previous app/login
      tokenManager.clearAll();

      const response = await authService.authenticate({
        username,
        password,
        selectedTenantId: tenantId,
        selectedApplicationId: appId,
      });

      if (!response.success || !response.data) {
        throw new Error(response.message || 'Authentication failed');
      }

      const authData = response.data;

      // Display role comes from the token's PRIMA *client* roles
      // (resource_access.prima.roles) — the role the user holds within this
      // application. TenantHub's authData.userRole is a tenant-level value
      // and is often empty, so prefer the client role and fall back to it.
      let displayRole = authData.userRole || '';
      try {
        const token = tokenManager.getAccessToken();
        if (token) {
          const payload = tokenManager.decodeToken(token);
          const resourceRoles: string[] = (payload as any)?.resource_access?.prima?.roles ?? [];
          const primaRole = pickPrimaRole(resourceRoles);
          if (primaRole) displayRole = formatRoleName(primaRole);
        }
      } catch {
        // Token parse failed — keep the authData.userRole fallback.
      }

      // Set React state
      setUser({
        userId: authData.userId || '',
        userName: authData.userName || '',
        userEmail: authData.userEmail || '',
        firstName: authData.userFirstName || '',
        lastName: authData.userLastName || '',
        fullName: [authData.userFirstName, authData.userMiddleName, authData.userLastName]
          .filter(Boolean)
          .join(' '),
        userRole: displayRole,
      });

      setTenantContext({ tenantId, appId });

      if (authData.permissions) {
        setPermissionContext(authData.permissions);
        setScopeConfiguration(authData.permissions.scopeConfiguration);

        // Build and store navigation for main_layout
        const navSections = buildNavigationFromPermissions(authData.permissions);
        localStorage.setItem(STORAGE_KEYS.NAVIGATION_MENU, JSON.stringify(navSections));

        const modulesConfig = buildModulesConfigFromPermissions(authData.permissions);
        localStorage.setItem(STORAGE_KEYS.MODULES_CONFIG, JSON.stringify(modulesConfig));

        // Hydrate scope selections from scopeConfiguration
        const scope = authData.permissions.scopeConfiguration;
        if (scope) {
          const primaryEntity =
            scope.accessibleEntities.find((e) => e.isPrimary) ||
            scope.accessibleEntities[0];
          if (primaryEntity) {
            setSelectedEntityState({
              id: primaryEntity.id,
              name: primaryEntity.entityName,
            });
          }

          const primaryBU =
            scope.accessibleBusinessUnits.find((b) => b.isPrimary) ||
            scope.accessibleBusinessUnits[0];
          if (primaryBU) {
            setSelectedBUState({ id: primaryBU.id, name: primaryBU.buName });
          }

          const currentFY =
            scope.accessibleFinancialYears.find((f) => f.isPrimary) ||
            scope.accessibleFinancialYears.find((f) => f.isCurrent) ||
            scope.accessibleFinancialYears[0];
          if (currentFY) {
            setSelectedFYState({
              id: currentFY.id,
              name: currentFY.fyName,
              code: currentFY.fyCode,
            });
          }
        }
      }

      // Store user object for legacy compatibility
      localStorage.setItem(
        STORAGE_KEYS.USER,
        JSON.stringify({
          id: authData.userId,
          username: authData.userName,
          email: authData.userEmail,
          role: displayRole,
        })
      );
    },
    []
  );

  const logout = useCallback(() => {
    if (getAuthMode() === 'tenant-hub') {
      authService.tenantHubLogout();
    } else {
      tokenManager.clearAll();
    }
    setUser(null);
    setTenantContext(null);
    setPermissionContext(null);
    setScopeConfiguration(null);
    setSelectedEntityState(null);
    setSelectedBUState(null);
    setSelectedFYState(null);
  }, []);

  const refreshPermissions = useCallback(async () => {
    const appId = tokenManager.getAppId();
    if (!appId) return;

    const response = await authService.refreshPermissions(appId);
    if (response.success && response.data) {
      setPermissionContext(response.data);
      setScopeConfiguration(response.data.scopeConfiguration);
      localStorage.setItem(STORAGE_KEYS.PERMISSIONS, JSON.stringify(response.data));
    }
  }, []);

  // === Scope Setters ===

  const setSelectedEntity = useCallback((id: string, name: string) => {
    setSelectedEntityState({ id, name });
    localStorage.setItem(STORAGE_KEYS.SELECTED_ENTITY_ID, id);
    localStorage.setItem(STORAGE_KEYS.SELECTED_ENTITY_NAME, name);
  }, []);

  const setSelectedBusinessUnit = useCallback((id: string, name: string) => {
    setSelectedBUState({ id, name });
    localStorage.setItem(STORAGE_KEYS.SELECTED_BU_ID, id);
    localStorage.setItem(STORAGE_KEYS.SELECTED_BU_NAME, name);
  }, []);

  const setSelectedFinancialYear = useCallback((id: string, name: string, code: string) => {
    setSelectedFYState({ id, name, code });
    localStorage.setItem(STORAGE_KEYS.SELECTED_FY_ID, id);
    localStorage.setItem(STORAGE_KEYS.SELECTED_FY_NAME, name);
    localStorage.setItem(STORAGE_KEYS.SELECTED_FY_CODE, code);
  }, []);

  // Accessible scope lists from scope configuration
  const accessibleEntities = scopeConfiguration?.accessibleEntities ?? [];
  const accessibleBusinessUnits = scopeConfiguration?.accessibleBusinessUnits ?? [];
  const accessibleFinancialYears = scopeConfiguration?.accessibleFinancialYears ?? [];

  // === Memoized Context Value ===
  const value = useMemo<AuthContextValue>(
    () => ({
      isAuthenticated,
      isLoading,
      user,
      tenantContext,
      permissionContext,
      scopeConfiguration,
      discover,
      authenticate,
      logout,
      refreshPermissions,
      permissionEngine,
      selectedEntity,
      selectedBusinessUnit,
      selectedFinancialYear,
      setSelectedEntity,
      setSelectedBusinessUnit,
      setSelectedFinancialYear,
      accessibleEntities,
      accessibleBusinessUnits,
      accessibleFinancialYears,
    }),
    [
      isAuthenticated,
      isLoading,
      user,
      tenantContext,
      permissionContext,
      scopeConfiguration,
      discover,
      authenticate,
      logout,
      refreshPermissions,
      permissionEngine,
      selectedEntity,
      selectedBusinessUnit,
      selectedFinancialYear,
      setSelectedEntity,
      setSelectedBusinessUnit,
      setSelectedFinancialYear,
      accessibleEntities,
      accessibleBusinessUnits,
      accessibleFinancialYears,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// ============================================================================
// Hook
// ============================================================================

export const useAuthContext = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
